import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Dedicated page for foreign law firms and in-house counsel seeking Italian
// local counsel / referral of enforcement matters. Institutional tone,
// deontologically compliant (no fee-splitting or outcome claims).

const BASE = 'https://belliardo-international.com';

const INSTRUCT = [
  'Enforcement in Italy of foreign judgments and payment orders (Brussels I bis, Lugano Convention, Italian private international law)',
  'Decreto ingiuntivo (Italian payment order) for documented claims',
  'Precautionary measures, including sequestro conservativo',
  'Italian civil and commercial litigation as local counsel',
  'Service of judicial and extrajudicial documents in Italy',
  'Debtor and asset investigations through the official Italian registers',
];

const STEPS = [
  'Written preliminary assessment — you send the file summary; we respond with a written first assessment of the procedural route, free of charge.',
  'Scope and fee proposal in writing — before any step is taken, you receive a written proposal in English covering scope, fees and foreseeable timeline.',
  'You keep the client relationship — we act as Italian counsel of record and report to you at each procedural step with written, timestamped updates in English, in accordance with the applicable professional rules.',
];

const NEED = [
  'The enforceable title (if any) or the underlying contract and invoices',
  'Evidence of service of the foreign proceedings, where relevant',
  'Client identification documents (Italian anti-money-laundering rules apply)',
  'Any known deadline, including limitation periods (prescrizione)',
  'A summary of the debtor and of any known assets in Italy',
];

export default function ForLawyers() {
  return (
    <div className="relative bg-ink-950 text-white min-h-screen" data-testid="for-lawyers-page">
      <Helmet>
        <title>Italian Local Counsel for Foreign Law Firms — Debt Recovery in Italy | Studio Legale Belliardo</title>
        <meta name="description" content="Italian counsel for foreign law firms: enforcement of foreign judgments in Italy, decreto ingiuntivo, precautionary measures and debtor investigations. Written reporting in English at every step." />
        <link rel="canonical" href={`${BASE}/for-lawyers`} />
      </Helmet>

      <header className="border-b border-white/8">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl md:text-2xl text-white leading-none" data-testid="for-lawyers-home-link">
            Belliardo<span className="font-mono text-white/40 text-[10px] ml-3 uppercase tracking-[0.25em]">International practice · Verona</span>
          </Link>
          <Link to="/articles" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white" data-testid="for-lawyers-journal-link">
            Journal →
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[900px] px-6 md:px-10 py-20 md:py-28">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40 mb-6">— For foreign counsel</p>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-white font-light mb-8">
          Italian counsel for your clients' claims in Italy.
        </h1>
        <p className="text-white/75 text-base md:text-lg leading-relaxed mb-16 max-w-[700px]">
          Your client has a claim against an Italian debtor. We act as your Italian legal partner — handling
          proceedings and enforcement before the Italian courts, and keeping you informed in writing at every stage.
        </p>

        <section className="mb-14">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mb-6">When firms instruct us</h2>
          <ul className="space-y-3">
            {INSTRUCT.map((s) => (
              <li key={s} className="text-white/75 text-base leading-relaxed flex gap-4">
                <span className="text-white/50 shrink-0">—</span>{s}
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-14">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mb-6">How the collaboration works</h2>
          <ol className="space-y-5">
            {STEPS.map((s, i) => (
              <li key={i} className="flex gap-5 items-start">
                <span className="font-mono text-[11px] text-white/50 tracking-[0.2em] pt-1 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-white/75 text-base leading-relaxed">{s}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mb-14">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mb-6">What we need for a first assessment</h2>
          <ul className="space-y-3">
            {NEED.map((s) => (
              <li key={s} className="text-white/75 text-base leading-relaxed flex gap-4">
                <span className="text-white/50 shrink-0">—</span>{s}
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-16 border-t border-white/10 pt-10">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mb-6">Professional rules</h2>
          <p className="text-white/75 text-base leading-relaxed max-w-[700px]">
            The firm practises under the Italian Code of Conduct for the legal profession (Codice Deontologico Forense).
            All communications received from instructing counsel are covered by professional secrecy under Italian law,
            regardless of whether a mandate is ultimately conferred. Correspondence is handled in English or Italian.
          </p>
        </section>

        <section className="border border-white/15 bg-ink-900 p-8 md:p-10" data-testid="for-lawyers-cta">
          <a href="mailto:avvocati@studiobelliardo.it?subject=Referral%20—%20claim%20against%20Italian%20debtor"
            className="inline-block font-mono text-[12px] uppercase tracking-[0.25em] bg-white text-ink-950 px-8 py-4 hover:bg-white/85 transition-colors"
            data-testid="for-lawyers-cta-button">
            Write to the desk →
          </a>
          <p className="text-white/50 text-sm mt-5 leading-relaxed">
            avvocati@studiobelliardo.it · Tel. +39 045.8011276 · Salita Fontana del Ferro 24, 37129 Verona.
            You may also use the <a href="/#claim-form" className="underline underline-offset-4 hover:text-white transition-colors">written claim form</a>.
          </p>
        </section>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap gap-x-8 gap-y-3 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="for-lawyers-privacy-link">Privacy Policy</Link>
          <Link to="/cookie-policy" className="hover:text-white transition-colors" data-testid="for-lawyers-cookie-link">Cookie Policy</Link>
        </div>
      </main>
    </div>
  );
}
